import { useMemo } from 'react';
import { useApp } from '@/context/AppContext';
import KpiCard from '@/components/base/KpiCard';
import { formatCurrency } from '@/utils/calculations';

export default function DashboardPage() {
  const { isDark, clients, transfers, items, sales, vendorPayments } = useApp();

  const kpis = useMemo(() => {
    const totalTransferred = transfers.reduce((s, t) => s + t.amount, 0);
    const totalMyCommission = transfers.reduce((s, t) => s + t.myCommission, 0);
    const totalProviderCommission = transfers.reduce((s, t) => s + t.providerCommission, 0);
    const totalProfit = transfers.reduce((s, t) => s + t.myProfit, 0);
    const totalClientBalance = clients.reduce((s, c) => s + c.balance, 0);
    const totalInventoryValue = items.reduce((s, i) => s + i.purchasePrice * i.currentStock, 0);
    const totalSales = sales.reduce((s, s2) => s + s2.totalSaleValue, 0);
    const grossProfit = sales.reduce((s, s2) => s + s2.grossProfit, 0);
    const netProfit = sales.reduce((s, s2) => s + s2.netProfit, 0);
    const totalOwedToVendors = items.reduce((s, i) => s + i.totalPurchaseValue, 0);
    const totalPaidToVendors = vendorPayments.reduce((s, p) => s + p.amount, 0);
    const vendorBalance = totalOwedToVendors - totalPaidToVendors;
    return { totalTransferred, totalMyCommission, totalProviderCommission, totalProfit, totalClientBalance, totalInventoryValue, totalSales, grossProfit, netProfit, totalOwedToVendors, totalPaidToVendors, vendorBalance };
  }, [transfers, clients, items, sales, vendorPayments]);

  const recentTransfers = transfers.slice(0, 5);
  const recentSales = sales.slice(0, 5);
  const lowStockItems = items.filter(i => i.currentStock <= i.lowStockAlert);

  const statusColor = (s: string) => {
    if (s === 'completed' || s === 'paid') return 'text-emerald-500';
    if (s === 'pending') return 'text-amber-500';
    return 'text-red-500';
  };

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* KPI Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        <KpiCard title="Total Transferred" value={formatCurrency(kpis.totalTransferred)} icon="ri-exchange-dollar-line" iconBg="bg-emerald-50" iconColor="text-emerald-500" subtitle="All time" />
        <KpiCard title="My Commission" value={formatCurrency(kpis.totalMyCommission)} icon="ri-percent-line" iconBg="bg-teal-50" iconColor="text-teal-500" />
        <KpiCard title="Provider Commission" value={formatCurrency(kpis.totalProviderCommission)} icon="ri-building-line" iconBg="bg-orange-50" iconColor="text-orange-500" />
        <KpiCard title="Transfer Profit" value={formatCurrency(kpis.totalProfit)} icon="ri-money-dollar-circle-line" iconBg="bg-green-50" iconColor="text-green-600" />
        <KpiCard title="Client Balances" value={formatCurrency(kpis.totalClientBalance)} icon="ri-group-line" iconBg="bg-violet-50" iconColor="text-violet-500" subtitle={`${clients.length} clients`} />
        <KpiCard title="Inventory Value" value={formatCurrency(kpis.totalInventoryValue)} icon="ri-store-2-line" iconBg="bg-sky-50" iconColor="text-sky-500" subtitle={`${items.length} items`} />
        <KpiCard title="Total Sales" value={formatCurrency(kpis.totalSales)} icon="ri-shopping-cart-2-line" iconBg="bg-pink-50" iconColor="text-pink-500" />
        <KpiCard title="Net Profit (Sales)" value={formatCurrency(kpis.netProfit)} icon="ri-line-chart-line" iconBg="bg-lime-50" iconColor="text-lime-600" />
        <KpiCard title="Vendor Balance Owed" value={formatCurrency(kpis.vendorBalance)} icon="ri-truck-line" iconBg="bg-red-50" iconColor="text-red-500" subtitle={`Paid: ${formatCurrency(kpis.totalPaidToVendors)}`} />
      </div>

      {/* Two column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Transfers */}
        <div className={`rounded-xl border p-5 ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-100'}`}>
          <div className="flex items-center justify-between mb-4">
            <h3 className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Recent Transfers</h3>
            <a href="/transfers" className="text-xs text-emerald-500 hover:underline cursor-pointer">View all</a>
          </div>
          {recentTransfers.length === 0 ? (
            <p className={`text-sm text-center py-6 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No transfers yet</p>
          ) : (
            <div className="space-y-3">
              {recentTransfers.map(t => (
                <div key={t.id} className={`flex items-center justify-between py-2 border-b last:border-0 ${isDark ? 'border-gray-800' : 'border-gray-50'}`}>
                  <div>
                    <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-800'}`}>{t.clientName}</p>
                    <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{t.date} · {t.transactionId}</p>
                  </div>
                  <div className="text-right">
                    <p className={`text-sm font-semibold ${t.type === 'client_paid_me' ? 'text-emerald-500' : 'text-red-500'}`}>
                      {t.type === 'client_paid_me' ? '+' : '-'}{formatCurrency(t.amount)}
                    </p>
                    <p className={`text-xs capitalize ${statusColor(t.status)}`}>{t.status}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent Sales */}
        <div className={`rounded-xl border p-5 ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-100'}`}>
          <div className="flex items-center justify-between mb-4">
            <h3 className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Recent Sales</h3>
            <a href="/sales" className="text-xs text-emerald-500 hover:underline cursor-pointer">View all</a>
          </div>
          {recentSales.length === 0 ? (
            <p className={`text-sm text-center py-6 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No sales yet</p>
          ) : (
            <div className="space-y-3">
              {recentSales.map(s => (
                <div key={s.id} className={`flex items-center justify-between py-2 border-b last:border-0 ${isDark ? 'border-gray-800' : 'border-gray-50'}`}>
                  <div>
                    <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-800'}`}>{s.itemName}</p>
                    <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{s.customerName} · qty {s.quantitySold}</p>
                  </div>
                  <div className="text-right">
                    <p className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{formatCurrency(s.totalSaleValue)}</p>
                    <p className="text-xs text-emerald-500">+{formatCurrency(s.netProfit)} profit</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Low Stock + Top Clients */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Low Stock Alerts */}
        <div className={`rounded-xl border p-5 ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-100'}`}>
          <div className="flex items-center gap-2 mb-4">
            <span className="w-5 h-5 flex items-center justify-center"><i className="ri-alert-line text-amber-500" /></span>
            <h3 className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Low Stock Alerts</h3>
            <span className="ml-auto bg-amber-100 text-amber-700 text-xs px-2 py-0.5 rounded-full">{lowStockItems.length}</span>
          </div>
          {lowStockItems.length === 0 ? (
            <p className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>All items are well stocked!</p>
          ) : (
            <div className="space-y-2">
              {lowStockItems.map(item => (
                <div key={item.id} className={`flex items-center justify-between p-3 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-amber-50'}`}>
                  <div>
                    <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-800'}`}>{item.name}</p>
                    <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>SKU: {item.sku}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold text-red-500">{item.currentStock} left</p>
                    <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Alert: {item.lowStockAlert}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Top Clients */}
        <div className={`rounded-xl border p-5 ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-100'}`}>
          <h3 className={`text-sm font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>Top Clients by Volume</h3>
          {clients.length === 0 ? (
            <p className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No clients yet</p>
          ) : (
            <div className="space-y-3">
              {[...clients].sort((a, b) => b.totalTransferred - a.totalTransferred).slice(0, 5).map((c, idx) => (
                <div key={c.id} className="flex items-center gap-3">
                  <span className={`w-6 h-6 flex items-center justify-center rounded-full text-xs font-bold
                    ${idx === 0 ? 'bg-yellow-100 text-yellow-700' : idx === 1 ? 'bg-gray-100 text-gray-600' : 'bg-orange-50 text-orange-600'}`}>
                    {idx + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium truncate ${isDark ? 'text-white' : 'text-gray-800'}`}>{c.name}</p>
                    <div className={`h-1.5 rounded-full mt-1 ${isDark ? 'bg-gray-800' : 'bg-gray-100'}`}>
                      <div className="h-1.5 rounded-full bg-emerald-500" style={{ width: `${Math.min(100, (c.totalTransferred / 50000) * 100)}%` }} />
                    </div>
                  </div>
                  <p className={`text-sm font-semibold whitespace-nowrap ${isDark ? 'text-white' : 'text-gray-900'}`}>{formatCurrency(c.totalTransferred)}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
